import React, { useState } from 'react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Separator } from "./ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Calendar, Factory, Clock, Users, AlertTriangle, CheckCircle, Play, Pause, Square, Edit, Eye, Trash2, Plus, Search, FileText } from "lucide-react";
import { toast } from "sonner";

interface ProductionOrder {
  id: string;
  orderNumber: string;
  partName: string;
  customer: string;
  quantity: number;
  startDate: string;
  endDate: string;
  status: 'Scheduled' | 'In Progress' | 'Completed' | 'On Hold' | 'Cancelled';
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  machineAssigned: string;
  operatorAssigned: string;
  progress: number;
}

interface ProductionPlanData {
  orderNumber: string;
  partName: string;
  partNumber: string;
  customer: string;
  quantity: number;
  startDate: string;
  endDate: string;
  priority: string;
  machineRequired: string;
  operatorRequired: string;
  estimatedHours: number;
  specialInstructions: string;
}

export function ProductionPlanningPage() {
  const [activeTab, setActiveTab] = useState('planning');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ProductionOrder | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);

  const [formData, setFormData] = useState<ProductionPlanData>({
    orderNumber: '',
    partName: '',
    partNumber: '',
    customer: '',
    quantity: 0,
    startDate: '',
    endDate: '',
    priority: '',
    machineRequired: '',
    operatorRequired: '',
    estimatedHours: 0,
    specialInstructions: ''
  });

  // Mock data for production orders
  const [productionOrders] = useState<ProductionOrder[]>([
    {
      id: 'PRO001',
      orderNumber: 'PRO-2024-001',
      partName: 'Engine Piston',
      customer: 'ABC Manufacturing Ltd.',
      quantity: 100,
      startDate: '2024-02-25',
      endDate: '2024-03-05',
      status: 'In Progress',
      priority: 'High',
      machineAssigned: 'CNC-01',
      operatorAssigned: 'John Smith',
      progress: 65
    },
    {
      id: 'PRO002',
      orderNumber: 'PRO-2024-002',
      partName: 'Brake Disc',
      customer: 'XYZ Industries Pvt. Ltd.',
      quantity: 50,
      startDate: '2024-03-01',
      endDate: '2024-03-10',
      status: 'Scheduled',
      priority: 'Medium',
      machineAssigned: 'Lathe-02',
      operatorAssigned: 'Mike Johnson',
      progress: 0
    },
    {
      id: 'PRO003',
      orderNumber: 'PRO-2024-003',
      partName: 'Transmission Gear',
      customer: 'Global Motors Inc.',
      quantity: 75,
      startDate: '2024-02-20',
      endDate: '2024-02-28',
      status: 'Completed',
      priority: 'Low',
      machineAssigned: 'Mill-03',
      operatorAssigned: 'Sarah Wilson',
      progress: 100
    },
    {
      id: 'PRO004',
      orderNumber: 'PRO-2024-004',
      partName: 'Suspension Spring',
      customer: 'Tech Solutions Corp.',
      quantity: 200,
      startDate: '2024-03-05',
      endDate: '2024-03-20',
      status: 'On Hold',
      priority: 'Urgent',
      machineAssigned: 'Press-01',
      operatorAssigned: 'David Brown',
      progress: 25
    }
  ]);

  const machines = ['CNC-01', 'CNC-02', 'Lathe-01', 'Lathe-02', 'Mill-01', 'Mill-02', 'Mill-03', 'Press-01', 'Press-02'];
  const operators = ['John Smith', 'Mike Johnson', 'Sarah Wilson', 'David Brown', 'Lisa Davis', 'Tom Anderson'];
  const customers = ['ABC Manufacturing Ltd.', 'XYZ Industries Pvt. Ltd.', 'Global Motors Inc.', 'Tech Solutions Corp.', 'Prime Engineering Ltd.'];

  const filteredOrders = productionOrders.filter(order =>
    order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.partName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.machineAssigned.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (field: keyof ProductionPlanData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault();
    // console.log('Production plan saved:', formData);
    toast.success('Production plan saved successfully!');
    setIsFormOpen(false);
  };

  const handleNewOrder = () => {
    setSelectedOrder(null);
    setIsViewMode(false);
    setIsFormOpen(true);
    setFormData({
      orderNumber: '',
      partName: '',
      partNumber: '',
      customer: '',
      quantity: 0,
      startDate: '',
      endDate: '',
      priority: '',
      machineRequired: '',
      operatorRequired: '',
      estimatedHours: 0,
      specialInstructions: ''
    });
  };

  const handleEditOrder = (order: ProductionOrder) => {
    setSelectedOrder(order);
    setIsViewMode(false);
    setIsFormOpen(true);
  };

  const handleViewOrder = (order: ProductionOrder) => {
    setSelectedOrder(order);
    setIsViewMode(true);
    setIsFormOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Scheduled': return 'bg-blue-100 text-blue-700';
      case 'In Progress': return 'bg-yellow-100 text-yellow-700';
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'On Hold': return 'bg-orange-100 text-orange-700';
      case 'Cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low': return 'bg-green-100 text-green-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'High': return 'bg-orange-100 text-orange-700';
      case 'Urgent': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Production Planning & Scheduling</CardTitle>
                <p className="text-gray-600 text-sm">Manage production orders and resource allocation</p>
              </div>
            </div>
            <Button
              onClick={handleNewOrder}
              className="bg-primary-500 text-gray-700 hover:bg-gray-50 shadow-lg"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Production Order
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200">
          <TabsTrigger value="planning" className="data-[state=active]:bg-primary-500 data-[state=active]:text-white">
            Production Planning
          </TabsTrigger>
          <TabsTrigger value="scheduling" className="data-[state=active]:bg-primary-500 data-[state=active]:text-white">
            Scheduling Dashboard
          </TabsTrigger>
          <TabsTrigger value="monitoring" className="data-[state=active]:bg-primary-500 data-[state=active]:text-white">
            Progress Monitoring
          </TabsTrigger>
        </TabsList>

        {/* Production Planning Tab */}
        <TabsContent value="planning" className="space-y-6">
          <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search production orders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                </div>
                <div className="text-sm text-gray-600">
                  {filteredOrders.length} of {productionOrders.length} orders
                </div>
              </div>

              <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead>Order Number</TableHead>
                      <TableHead>Part Details</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Schedule</TableHead>
                      <TableHead>Resources</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} className="hover:bg-primary-50/50">
                        <TableCell className="font-medium">{order.orderNumber}</TableCell>
                        <TableCell>{order.partName}</TableCell>
                        <TableCell>{order.customer}</TableCell>
                        <TableCell>{order.quantity} units</TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3 w-3 text-gray-400" />
                              <span>{new Date(order.startDate).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-3 w-3 text-gray-400" />
                              <span>{new Date(order.endDate).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div>Machine: {order.machineAssigned}</div>
                            <div>Operator: {order.operatorAssigned}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getPriorityColor(order.priority)} border-0 text-xs`}>
                            {order.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusColor(order.status)} border-0`}>
                            {order.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300"
                                style={{ width: `${order.progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600">{order.progress}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewOrder(order)}
                              className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditOrder(order)}
                              className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduling Dashboard Tab */}
        <TabsContent value="scheduling" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {machines.slice(0, 6).map((machine, index) => (
              <Card key={machine} className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Factory className="h-5 w-5 text-primary-600" />
                    {machine}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Status:</span>
                      <Badge className={index % 2 === 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                        {index % 2 === 0 ? 'Available' : 'In Use'}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Utilization:</span>
                      <span className="text-sm font-medium">{Math.floor(Math.random() * 40) + 60}%</span>
                    </div>
                    {index % 2 !== 0 && (
                      <div className="text-sm">
                        <div className="text-gray-600">Current Order:</div>
                        <div className="font-medium text-primary-700">PRO-2024-00{index + 1}</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Progress Monitoring Tab */}
        <TabsContent value="monitoring" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Orders</p>
                    <p className="text-2xl font-semibold text-primary-700">{productionOrders.length}</p>
                  </div>
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                    <Factory className="h-5 w-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">In Progress</p>
                    <p className="text-2xl font-semibold text-yellow-700">
                      {productionOrders.filter(o => o.status === 'In Progress').length}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
                    <Play className="h-5 w-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Completed</p>
                    <p className="text-2xl font-semibold text-green-700">
                      {productionOrders.filter(o => o.status === 'Completed').length}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">On Hold</p>
                    <p className="text-2xl font-semibold text-orange-700">
                      {productionOrders.filter(o => o.status === 'On Hold').length}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                    <Pause className="h-5 w-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Production Order Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isViewMode ? 'View Production Order' : selectedOrder ? 'Edit Production Order' : 'Create New Production Order'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="orderNumber">Order Number</Label>
                <Input
                  id="orderNumber"
                  value={formData.orderNumber}
                  onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                  placeholder="Enter order number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partName">Part Name</Label>
                <Input
                  id="partName"
                  value={formData.partName}
                  onChange={(e) => handleInputChange('partName', e.target.value)}
                  placeholder="Enter part name"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partNumber">Part Number</Label>
                <Input
                  id="partNumber"
                  value={formData.partNumber}
                  onChange={(e) => handleInputChange('partNumber', e.target.value)}
                  placeholder="Enter part number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer">Customer</Label>
                <Select value={formData.customer} onValueChange={(value) => handleInputChange('customer', value)} >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search...">
                    {customers.map((customer) => (
                      <SelectItem key={customer} value={customer}>
                        {customer}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 0)}
                  placeholder="Enter quantity"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)} >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search...">
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="machineRequired">Machine Required</Label>
                <Select value={formData.machineRequired} onValueChange={(value) => handleInputChange('machineRequired', value)} >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                    <SelectValue placeholder="Select machine" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search...">
                    {machines.map((machine) => (
                      <SelectItem key={machine} value={machine}>
                        {machine}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="operatorRequired">Operator Required</Label>
                <Select value={formData.operatorRequired} onValueChange={(value) => handleInputChange('operatorRequired', value)} >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                    <SelectValue placeholder="Select operator" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search...">
                    {operators.map((operator) => (
                      <SelectItem key={operator} value={operator}>
                        {operator}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="estimatedHours">Estimated Hours</Label>
                <Input
                  id="estimatedHours"
                  value={formData.estimatedHours}
                  onChange={(e) => handleInputChange('estimatedHours', parseFloat(e.target.value) || 0)}
                  placeholder="Enter estimated hours"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="specialInstructions">Special Instructions</Label>
                <Input
                  id="specialInstructions"
                  value={formData.specialInstructions}
                  onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                  placeholder="Enter any special instructions"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsFormOpen(false)}
                className="border-primary-200 hover:bg-primary-50"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg"
              >
                Save Production Order
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}